import { useEffect, useState } from "react";
import {
  CheckCircle2,
  CircleAlert,
  Loader2,
  Trash2,
  UserCircle,
} from "lucide-react";
import { toast } from "sonner";
import { ArbiterShell } from "@/components/ArbiterShell";
import { AvocatShell } from "@/components/AvocatShell";
import { AssistantIAPanel } from "@/components/AssistantIAPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  deleteMyAiData,
  getApiErrorDetail,
  getMyProfile,
  type ProfileOut,
  type RoleArbitrage,
  updateMyProfile,
} from "@/lib/api";
import {
  type ApiErrorDebug,
  classifyApiError,
  formatApiDebug,
} from "@/hooks/useApiErrorState";
import { useRole } from "@/contexts/AuthContext";

interface ProfilError {
  message: string;
  debug?: ApiErrorDebug;
}

// Référentiels avocat (CCJA) — listes proposées (la base reste permissive).
const INSTITUTIONS_ARBITRAGE = ["CCJA", "CCI", "CIRDI", "Ad hoc"];
const SECTEURS = [
  "Énergie",
  "Mines",
  "Construction",
  "Finance",
  "Télécoms",
  "Transport",
];
const PAYS_OHADA = [
  "Bénin",
  "Burkina Faso",
  "Cameroun",
  "Centrafrique",
  "Comores",
  "Congo",
  "Côte d'Ivoire",
  "Gabon",
  "Guinée",
  "Guinée-Bissau",
  "Guinée équatoriale",
  "Mali",
  "Niger",
  "RDC",
  "Sénégal",
  "Tchad",
  "Togo",
];
const ROLE_ARBITRAGE_OPTIONS: { value: RoleArbitrage; label: string }[] = [
  { value: "ARBITRE", label: "Arbitre" },
  { value: "CONSEIL", label: "Conseil (avocat représentant)" },
  { value: "LES_DEUX", label: "Les deux" },
];

function ChipGroup({
  options,
  selected,
  onToggle,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const active = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className="text-xs rounded-full px-3 py-1 border transition-colors"
            style={{
              borderColor: active ? "var(--proof-gold)" : "var(--graphite-700)",
              background: active
                ? "color-mix(in oklch, var(--proof-gold) 18%, transparent)"
                : "transparent",
              color: active ? "var(--proof-gold)" : "var(--arbitre-slate-300)",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default function Profil() {
  const role = useRole();
  const [profile, setProfile] = useState<ProfileOut | null>(null);
  const [error, setError] = useState<ProfilError | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [purging, setPurging] = useState(false);

  const [fullName, setFullName] = useState("");
  const [yearsExp, setYearsExp] = useState<string>("");

  // Champs avocat (CCJA) — édités uniquement dans la vue PARTIE.
  const [titre, setTitre] = useState("");
  const [barreaux, setBarreaux] = useState("");
  const [roleArbitrage, setRoleArbitrage] = useState<RoleArbitrage | "">("");
  const [institutions, setInstitutions] = useState<string[]>([]);
  const [secteurs, setSecteurs] = useState<string[]>([]);
  const [paysOhada, setPaysOhada] = useState<string[]>([]);
  const [accepteNominations, setAccepteNominations] = useState(false);

  const toggleIn = (
    list: string[],
    setList: (v: string[]) => void,
    value: string
  ) =>
    setList(
      list.includes(value) ? list.filter(x => x !== value) : [...list, value]
    );

  const reload = async () => {
    try {
      const p = await getMyProfile();
      setProfile(p);
      setFullName(p.full_name);
      setYearsExp(
        p.years_of_experience !== null ? String(p.years_of_experience) : ""
      );
      setTitre(p.titre ?? "");
      setBarreaux(p.barreaux ?? "");
      setRoleArbitrage(p.role_arbitrage ?? "");
      setInstitutions(p.institutions_arbitrage ?? []);
      setSecteurs(p.secteurs ?? []);
      setPaysOhada(p.pays_ohada ?? []);
      setAccepteNominations(p.accepte_nominations ?? false);
      setError(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[Profil] getMyProfile failed:", err);
      const c = classifyApiError(err);
      setError({
        message:
          c.kind !== "none"
            ? c.detail
            : (getApiErrorDetail(err) ?? "Echec de chargement du profil."),
        debug: "debug" in c ? c.debug : undefined,
      });
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const onSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const updated = await updateMyProfile({
        full_name: fullName.trim(),
        years_of_experience: yearsExp
          ? Number.parseInt(yearsExp, 10)
          : undefined,
        // Champs avocat : envoyés uniquement dans la vue PARTIE.
        ...(role === "PARTIE"
          ? {
              titre: titre.trim() || undefined,
              barreaux: barreaux.trim() || undefined,
              role_arbitrage: roleArbitrage || undefined,
              institutions_arbitrage: institutions,
              secteurs,
              pays_ohada: paysOhada,
              accepte_nominations: accepteNominations,
            }
          : {}),
      });
      setProfile(updated);
      toast.success("Profil mis à jour.");
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? "Echec mise à jour.");
    } finally {
      setSavingProfile(false);
    }
  };

  const onDeleteAiData = async () => {
    setPurging(true);
    try {
      await deleteMyAiData();
      toast.success(
        "Droit à l'oubli exercé. Vos logs IA ont été supprimés (RGPD Art. 17)."
      );
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? "Echec de la suppression.");
    } finally {
      setPurging(false);
    }
  };

  const content = (
    <div className="flex-1 px-4 sm:px-6 py-6 sm:py-8 max-w-2xl mx-auto w-full space-y-6 sm:space-y-8">
      {profile === null && !error && (
        <div className="flex items-center justify-center py-16">
          <Loader2
            className="animate-spin"
            size={28}
            style={{ color: "var(--proof-gold)" }}
          />
        </div>
      )}

      {error && (
        <div
          className="rounded-md border p-6 text-sm space-y-3"
          style={{
            borderColor:
              "color-mix(in oklch, var(--block-red) 40%, transparent)",
            background:
              "color-mix(in oklch, var(--block-red) 10%, transparent)",
            color: "rgb(254 202 202)",
          }}
        >
          <p>{error.message}</p>
          {error.debug && (
            <details>
              <summary
                className="text-[11px] cursor-pointer"
                style={{ color: "rgb(254 202 202)" }}
              >
                Details techniques
              </summary>
              <pre
                className="mt-1.5 text-[10px] whitespace-pre-wrap break-all gsf-mono-proof rounded-md p-2"
                style={{
                  background: "var(--graphite-900)",
                  color: "var(--arbitre-slate-300)",
                  border: "1px solid var(--graphite-700)",
                }}
              >
                {formatApiDebug(error.debug)}
              </pre>
            </details>
          )}
          <Button size="sm" variant="outline" onClick={() => void reload()}>
            Reessayer
          </Button>
        </div>
      )}

      {profile && (
        <>
          <section>
            <div
              className="flex items-center gap-2 mb-3 text-caption-arbitre"
              style={{ color: "var(--proof-gold)" }}
            >
              <UserCircle size={12} />
              Identité
            </div>
            <div className="gsf-surface p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="full_name"
                    className="text-caption-arbitre"
                    style={{ color: "var(--proof-gold)" }}
                  >
                    Nom complet
                  </Label>
                  <Input
                    id="full_name"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="years"
                    className="text-caption-arbitre"
                    style={{ color: "var(--proof-gold)" }}
                  >
                    Années d'expérience
                  </Label>
                  <Input
                    id="years"
                    type="number"
                    min="0"
                    max="60"
                    value={yearsExp}
                    onChange={e => setYearsExp(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <div
                className="flex flex-wrap items-center gap-3 text-xs"
                style={{ color: "var(--arbitre-slate-300)" }}
              >
                <span>
                  {profile.is_verified ? (
                    <>
                      <CheckCircle2
                        size={11}
                        className="inline mr-1"
                        style={{ color: "var(--validate-green)" }}
                      />
                      Profil vérifié
                      {profile.verified_at &&
                        ` le ${new Date(profile.verified_at).toLocaleDateString("fr-FR")}`}
                    </>
                  ) : (
                    <>
                      <CircleAlert
                        size={11}
                        className="inline mr-1"
                        style={{ color: "var(--alert-orange)" }}
                      />
                      En attente de validation administrateur (Art. 52 AI Act).
                    </>
                  )}
                </span>
              </div>

              {role === "PARTIE" && (
                <div
                  className="space-y-4 pt-4"
                  style={{ borderTop: "1px solid var(--graphite-700)" }}
                >
                  <div
                    className="text-caption-arbitre"
                    style={{ color: "var(--proof-gold)" }}
                  >
                    Profil avocat — conformité CCJA
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="titre"
                        className="text-caption-arbitre"
                        style={{ color: "var(--proof-gold)" }}
                      >
                        Titre professionnel
                      </Label>
                      <Input
                        id="titre"
                        value={titre}
                        onChange={e => setTitre(e.target.value)}
                        placeholder="Avocat à la Cour, Arbitrage international"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label
                        htmlFor="barreaux"
                        className="text-caption-arbitre"
                        style={{ color: "var(--proof-gold)" }}
                      >
                        Barreau(x)
                      </Label>
                      <Input
                        id="barreaux"
                        value={barreaux}
                        onChange={e => setBarreaux(e.target.value)}
                        placeholder="Paris, Abidjan…"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <div
                      className="text-caption-arbitre mb-1.5"
                      style={{ color: "var(--proof-gold)" }}
                    >
                      Rôle en arbitrage
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {ROLE_ARBITRAGE_OPTIONS.map(opt => {
                        const active = roleArbitrage === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              setRoleArbitrage(active ? "" : opt.value)
                            }
                            className="text-xs rounded-full px-3 py-1 border transition-colors"
                            style={{
                              borderColor: active
                                ? "var(--proof-gold)"
                                : "var(--graphite-700)",
                              background: active
                                ? "color-mix(in oklch, var(--proof-gold) 18%, transparent)"
                                : "transparent",
                              color: active
                                ? "var(--proof-gold)"
                                : "var(--arbitre-slate-300)",
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div
                      className="text-caption-arbitre mb-1.5"
                      style={{ color: "var(--proof-gold)" }}
                    >
                      Institutions d'arbitrage
                    </div>
                    <ChipGroup
                      options={INSTITUTIONS_ARBITRAGE}
                      selected={institutions}
                      onToggle={v => toggleIn(institutions, setInstitutions, v)}
                    />
                  </div>

                  <div>
                    <div
                      className="text-caption-arbitre mb-1.5"
                      style={{ color: "var(--proof-gold)" }}
                    >
                      Secteurs
                    </div>
                    <ChipGroup
                      options={SECTEURS}
                      selected={secteurs}
                      onToggle={v => toggleIn(secteurs, setSecteurs, v)}
                    />
                  </div>

                  <div>
                    <div
                      className="text-caption-arbitre mb-1.5"
                      style={{ color: "var(--proof-gold)" }}
                    >
                      Pays OHADA couverts
                    </div>
                    <ChipGroup
                      options={PAYS_OHADA}
                      selected={paysOhada}
                      onToggle={v => toggleIn(paysOhada, setPaysOhada, v)}
                    />
                  </div>

                  <label
                    className="flex items-center gap-2 text-sm cursor-pointer"
                    style={{ color: "var(--arbitre-slate-300)" }}
                  >
                    <input
                      type="checkbox"
                      checked={accepteNominations}
                      onChange={e => setAccepteNominations(e.target.checked)}
                    />
                    J'accepte les nominations en tant qu'arbitre
                  </label>
                </div>
              )}

              <Button
                onClick={onSaveProfile}
                disabled={savingProfile}
                style={{
                  background: "var(--proof-gold)",
                  color: "var(--graphite-900)",
                }}
              >
                {savingProfile ? (
                  <>
                    <Loader2 size={14} className="animate-spin mr-2" />
                    Enregistrement…
                  </>
                ) : (
                  "Enregistrer"
                )}
              </Button>
            </div>
          </section>

          <section>
            <div
              className="flex items-center gap-2 mb-3 text-caption-arbitre"
              style={{ color: "var(--proof-gold)" }}
            >
              <Trash2 size={12} />
              Données IA — droit à l'oubli
            </div>
            <div className="gsf-surface p-5">
              <p
                className="text-sm"
                style={{
                  color: "var(--arbitre-slate-300)",
                  lineHeight: 1.55,
                }}
              >
                Conformément au RGPD Art. 17 et à l'APDP Art. 17, vous pouvez
                exiger la suppression de tous vos logs d'interaction IA
                (questions soumises au moteur RAG, réponses générées, sources
                utilisées). L'opération est idempotente et irréversible : les
                preuves probatoires (TSA + Merkle des pièces) ne sont pas
                affectées.
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="mt-4"
                    style={{ color: "var(--block-red)" }}
                  >
                    <Trash2 size={14} className="mr-2" />
                    Effacer mes logs IA
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Confirmer l'effacement RGPD ?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Vos questions, les réponses générées et les sources
                      associées seront effacées du registre IA. Cette action est
                      irréversible. Les pièces probatoires déposées au dossier
                      ne sont pas impactées.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => void onDeleteAiData()}
                      disabled={purging}
                    >
                      {purging ? "Suppression…" : "Confirmer"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </section>
        </>
      )}
    </div>
  );

  // L'avocat (PARTIE) accède à son profil depuis le Cockpit Avocat : on le
  // garde dans son shell dédié au lieu de l'enfermer dans le shell arbitre.
  if (role === "PARTIE") {
    return <AvocatShell>{content}</AvocatShell>;
  }

  return (
    <ArbiterShell title="Mon profil" rightPanel={<AssistantIAPanel />}>
      {content}
    </ArbiterShell>
  );
}
